import { ReactElement, useMemo, useCallback } from 'react';
import { Stack, Typography, Avatar } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTruckFast as VehicleIcon,
  faVectorSquare as LogisticsZonesIcon,
  faDollarSign as PriceIcon,
} from '@fortawesome/free-solid-svg-icons';
import { compact } from 'lodash';

import { OrderActiveFilters } from '~components/index';
import { OrderActiveFiltersItem } from '~components/OrderActiveFilters/types';
import { Nullable } from '~globals/types/commons';
import { formatterDate, formatCurrencyValue } from '~utils/formatter';
import { getDefaultAvatar } from '~utils/user';
import { ORDERS_GLOBALS_OPTIONS } from '~constants/ordersStatesOptions';

import { useDialogOrderSearchContext } from './Context';
import { DEFAULT_PAGINATION, DEFAULT_FILTERS_STATE } from './utils';

const DialogOrderSearchCurrentFilters = (): Nullable<ReactElement> => {
  const {
    selectsData,
    filters: {
      filters,
      setFilterByKey,
      parseFiltersRequest,
      setIsFilterApplied,
      clearFilters,
      filtersActives,
      setFilterActiveByKey,
      clearFiltersActives,
    },
    table: { handleChangeTable, handleInitialTable },
  } = useDialogOrderSearchContext();

  const isLastFilterActive = useMemo(() => {
    const currentFilters = [
      filters.search,
      filters.expirationDate,
      filters.dateFrom,
      filters.dateTo,
      filters.states?.length,
      filters.vehicles?.length,
      filters.drivers?.length,
      filters.services?.length,
      filters.zones?.length,
      filters.priceGt,
      filters.priceLt,
      filters.assignedUser,
    ];

    const compactCurrentFilters = compact(currentFilters);

    return compactCurrentFilters.length === 1;
  }, [filters]);

  const handleClearFilterByKey = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (key: string, newVal: any) => {
      setFilterByKey(key, newVal);
      setFilterActiveByKey(key, newVal);

      if (isLastFilterActive) {
        handleInitialTable();
        return;
      }

      handleChangeTable('filter', {
        pagination: DEFAULT_PAGINATION,
        filters: parseFiltersRequest({ [key]: newVal }),
      });
    },
    [
      setFilterByKey,
      setFilterActiveByKey,
      handleChangeTable,
      parseFiltersRequest,
      isLastFilterActive,
      handleInitialTable,
    ],
  );

  const handleClearFilterRangeDates = useCallback(() => {
    setFilterByKey('dateFrom', null);
    setFilterActiveByKey('dateFrom', null);

    setFilterByKey('dateTo', null);
    setFilterActiveByKey('dateTo', null);

    if (isLastFilterActive) {
      handleInitialTable();
      return;
    }

    handleChangeTable('filter', {
      pagination: DEFAULT_PAGINATION,
      filters: parseFiltersRequest({ dateFrom: null, dateTo: null }),
    });
  }, [
    setFilterByKey,
    setFilterActiveByKey,
    handleChangeTable,
    parseFiltersRequest,
    isLastFilterActive,
    handleInitialTable,
  ]);

  const handleClearRangeNumbers = useCallback(
    (keyGt: string, keyLt: string) => {
      setFilterByKey(keyGt, '');
      setFilterActiveByKey(keyGt, '');

      setFilterByKey(keyLt, '');
      setFilterActiveByKey(keyLt, '');

      if (isLastFilterActive) {
        handleInitialTable();
        return;
      }

      handleChangeTable('filter', {
        pagination: DEFAULT_PAGINATION,
        filters: parseFiltersRequest({ [keyGt]: '', [keyLt]: '' }),
      });
    },
    [
      setFilterByKey,
      setFilterActiveByKey,
      handleChangeTable,
      parseFiltersRequest,
      isLastFilterActive,
      handleInitialTable,
    ],
  );

  const handleClearFilters = useCallback(() => {
    clearFilters(DEFAULT_FILTERS_STATE);
    clearFiltersActives();

    selectsData.drivers.autocompleteRef.current?.resetAutocomplete();
    selectsData.vehicles.autocompleteRef.current?.resetAutocomplete();
    selectsData.services.autocompleteRef.current?.resetAutocomplete();
    selectsData.logisticsZones.autocompleteRef.current?.resetAutocomplete();
    selectsData.states.autocompleteRef.current?.resetAutocomplete();
    selectsData.users.autocompleteRef.current?.resetAutocomplete();

    handleInitialTable();

    setIsFilterApplied(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clearFilters,
    clearFiltersActives,
    handleInitialTable,
    setIsFilterApplied,
  ]);

  const activeFilters = useMemo<OrderActiveFiltersItem[]>(() => {
    const currentFilters: OrderActiveFiltersItem[] = [];

    if (filtersActives.search) {
      currentFilters.push({
        id: 'search',
        label: filtersActives.search,
        tooltip: 'Nro. Orden / Nro. Tracking / Grupo',
        onClear: () => handleClearFilterByKey('search', ''),
      });
    }

    const price = compact([filtersActives.priceGt, filtersActives.priceLt]);

    if (price.length > 0) {
      const priceLabel = price
        .map((currentPrice) =>
          formatCurrencyValue(Number(currentPrice), { style: 'decimal' }),
        )
        .join(' - ');

      currentFilters.push({
        id: 'price',
        label: (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <FontAwesomeIcon
              icon={PriceIcon}
              style={{ width: 15, height: 15 }}
            />

            <Typography variant="inherit">{priceLabel}</Typography>
          </Stack>
        ),
        tooltip: 'Precio',
        onClear: () => handleClearRangeNumbers('priceGt', 'priceLt'),
      });
    }

    if (filtersActives.expirationDate) {
      currentFilters.push({
        id: 'expirationDate',
        label: formatterDate(filtersActives.expirationDate, {
          format: 'DD/MM/YYYY',
        }),
        tooltip: 'Fecha de vencimiento',
        onClear: () => handleClearFilterByKey('expirationDate', null),
      });
    }

    const rangeDates = compact([
      filtersActives.dateFrom,
      filtersActives.dateTo,
    ]);

    if (rangeDates.length > 0) {
      const rangeLabel = rangeDates
        .map((date) =>
          formatterDate(date, {
            format: 'DD/MM/YYYY',
          }),
        )
        .join(' - ');

      currentFilters.push({
        id: 'rangeDates',
        label: rangeLabel,
        tooltip: 'Rango de fechas',
        onClear: () => handleClearFilterRangeDates(),
      });
    }

    if (filtersActives.states !== null && filtersActives.states.length > 0) {
      const currentStates = ORDERS_GLOBALS_OPTIONS.filter((state) =>
        filtersActives.states.includes(state.value as string),
      )
        .map((state) => state.label)
        .join(' / ');

      currentFilters.push({
        id: 'states',
        label: currentStates,
        tooltip: 'Estados',
        onClear: () => {
          selectsData.states.autocompleteRef.current?.resetAutocomplete();
          handleClearFilterByKey('states', null);
        },
      });
    }

    if (
      filtersActives.vehicles !== null &&
      filtersActives.vehicles.length > 0
    ) {
      const vehiclesCount = filtersActives.vehicles.length - 1;

      const vehiclesLabel = filtersActives.vehicles.map(
        (vehicle, vehicleIndex) => (
          <Stack
            key={`filters-active-vehicle-${vehicle.id}`}
            direction="row"
            spacing={0.5}
            alignItems="center"
          >
            <FontAwesomeIcon
              icon={VehicleIcon}
              style={{ color: vehicle.color, width: 15, height: 15 }}
            />

            <Typography variant="inherit">{vehicle.displayName}</Typography>

            {vehicleIndex < vehiclesCount && (
              <Typography variant="inherit">/</Typography>
            )}
          </Stack>
        ),
      );

      currentFilters.push({
        id: 'vehicles',
        label: vehiclesLabel,
        tooltip: 'Vehículos',
        onClear: () => {
          selectsData.vehicles.autocompleteRef.current?.resetAutocomplete();
          handleClearFilterByKey('vehicles', null);
        },
      });
    }

    if (filtersActives.drivers !== null && filtersActives.drivers.length > 0) {
      const driversCount = filtersActives.drivers.length - 1;

      const driversLabel = filtersActives.drivers.map((driver, driverIndex) => (
        <Stack
          key={`filters-active-driver-${driver.id}`}
          direction="row"
          spacing={0.5}
          alignItems="center"
        >
          <Avatar
            src={getDefaultAvatar(driver.avatar ?? undefined)}
            alt={driver.displayName}
            sx={{ width: 15, height: 15 }}
          />

          <Typography variant="inherit">{driver.displayName}</Typography>

          {driverIndex < driversCount && (
            <Typography variant="inherit">/</Typography>
          )}
        </Stack>
      ));

      currentFilters.push({
        id: 'drivers',
        label: driversLabel,
        tooltip: 'Choferes',
        onClear: () => {
          selectsData.drivers.autocompleteRef.current?.resetAutocomplete();
          handleClearFilterByKey('drivers', null);
        },
      });
    }

    if (
      filtersActives.services !== null &&
      filtersActives.services.length > 0
    ) {
      const servicesLabel = filtersActives.services
        .map((service) => service.label.toString())
        .join(' / ');

      currentFilters.push({
        id: 'services',
        label: servicesLabel,
        tooltip: 'Tipos de servicios',
        onClear: () => {
          selectsData.services.autocompleteRef.current?.resetAutocomplete();
          handleClearFilterByKey('services', null);
        },
      });
    }

    if (filtersActives.zones !== null && filtersActives.zones.length > 0) {
      const zonesCount = filtersActives.zones.length - 1;

      const zonesLabel = filtersActives.zones.map((zone, zoneIndex) => (
        <Stack
          key={`filters-active-zone-${zone.zoneId}`}
          direction="row"
          spacing={0.5}
          alignItems="center"
        >
          {zone.color && (
            <FontAwesomeIcon
              icon={LogisticsZonesIcon}
              style={{ color: zone.color, width: 15, height: 15 }}
            />
          )}

          <Typography variant="inherit">{zone.code}</Typography>

          {zoneIndex < zonesCount && (
            <Typography variant="inherit">/</Typography>
          )}
        </Stack>
      ));

      currentFilters.push({
        id: 'zones',
        label: zonesLabel,
        tooltip: 'Zonas logísticas',
        onClear: () => {
          selectsData.logisticsZones.autocompleteRef.current?.resetAutocomplete();
          handleClearFilterByKey('zones', null);
        },
      });
    }

    if (filtersActives.assignedUser) {
      // eslint-disable-next-line max-len
      const assignedUserName = `${filtersActives.assignedUser.lastName}, ${filtersActives.assignedUser.firstName}`;

      currentFilters.push({
        id: 'assignedUser',
        label: (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Avatar
              src={getDefaultAvatar(
                filtersActives.assignedUser.avatarUrl || undefined,
              )}
              alt={assignedUserName}
              sx={{ width: 15, height: 15 }}
            />

            <Typography variant="inherit">{assignedUserName}</Typography>
          </Stack>
        ),
        tooltip: 'Usuario asignado',
        onClear: () => {
          selectsData.users.autocompleteRef.current?.resetAutocomplete();
          handleClearFilterByKey('assignedUser', null);
        },
      });
    }

    return currentFilters;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersActives, handleClearFilterByKey, handleClearFilterRangeDates]);

  if (activeFilters.length === 0) return null;

  return (
    <OrderActiveFilters
      filters={activeFilters}
      onClearFilters={handleClearFilters}
    />
  );
};

export default DialogOrderSearchCurrentFilters;
